/**
 * The Microsoft Secure Score section of the Defender board (L1) — the one
 * Defender figure with real history behind it.
 *
 * Microsoft keeps daily snapshots, so this is the only panel on this board that
 * can honestly be drawn as a trend. Everything else is a point-in-time read, and
 * drawing a line through point-in-time reads would manufacture a history NOVA
 * does not have.
 *
 * The honesty work in this file:
 *
 * - **A missing day is a gap, not a dip.** The trend is projected onto a dense
 *   day axis and a day Microsoft has no snapshot for carries `null`, so Recharts
 *   (`connectNulls` off) breaks the line. Interpolating would invent a score;
 *   dropping to zero would invent a collapse.
 * - **`score_delta: null` is not "no change".** It means fewer than two
 *   snapshots were read, so nothing was compared — a far weaker statement than
 *   stability, and the two must not render alike.
 * - **`max_score: null` on a control is not a fully-achieved control.** It means
 *   the control-profile read failed, so the gap is unknown and the control cannot
 *   be ranked at all. Those controls are held out of the ranking and counted
 *   separately rather than plotted at zero gap, which would rank them best.
 * - **This panel degrades in halves.** A 403 or 5xx on the control-profile
 *   endpoint leaves the score and its trend rendering with `worst_controls`
 *   empty and `controls_total` null, and lands the failure in `errors[]`. One
 *   refused read never blanks the panel.
 * - **Coverage beside the number** (§5). `licensed_user_count` is the population
 *   Microsoft measured the score across, and it is the usual explanation for a
 *   move nobody configured: onboard three hundred people and the same
 *   configuration scores differently. It is stated next to the score, and its
 *   change over the window is stated with it — `null`, never `0`, when fewer
 *   than two snapshots reported a count.
 * - **A ranking is not a plan, so the points are also grouped by effort.**
 *   "12 points available in AzureAD controls at Low user impact" is a sentence
 *   someone can schedule; a list of control ids is not. The grouping comes off
 *   the whole ranking rather than its truncated head, so the points add up.
 */

import { Chart } from "@norican/dataviz";
import { Alert, EmptyState, Meter, Statistic } from "@norican/react";

import { ChartCard } from "@/components/board";
import type {
  DefenderControlGroup,
  DefenderControlScore,
  DefenderScorePoint,
  DefenderSecureScore,
} from "@/lib/api";

import { DefenderErrorsAlert } from "./access-panel";
import { PanelCard, SectionHeading } from "./panel-card";
import { SECURE_SCORE_ANCHOR, TWIN_IDS } from "./params";
import {
  effortAxisLabel,
  effortLabel,
  formatCount,
  formatDay,
  formatPercent,
  formatPoints,
  formatPointsDelta,
  formatWindow,
  tooltipValue,
} from "./status";

/** How many controls the L1 ranking plots; the rest are in the table twin. */
const RANKED_LIMIT = 10;

/** One plotted day of the score trend. `score_pct: null` draws a gap. */
interface ScoreRow {
  day: string;
  label: string;
  score_pct: number | null;
  current_score: number | null;
  max_score: number | null;
  licensed_user_count: number | null;
}

/** One ranked control, with the points it is leaving on the table. */
interface ControlRow {
  control: string;
  label: string;
  category: string;
  score: number | null;
  max_score: number | null;
  score_gap: number;
  service: string;
  tier: string;
  user_impact: string;
  implementation_cost: string;
}

/** One product-and-effort group, with the points it is holding. */
interface EffortRow {
  key: string;
  label: string;
  sentence: string;
  service: string;
  user_impact: string;
  controls: number;
  points_available: number;
}

interface SecureScoreSectionProps {
  secureScore: DefenderSecureScore;
}

export function SecureScoreSection({ secureScore }: SecureScoreSectionProps) {
  const windowLabel = formatWindow(secureScore.window);
  const staleAfterMs = secureScore.cache_ttl_sec * 2000;
  const since = secureScore.collected_at;

  const trendRows = buildScoreRows(secureScore.trend);
  const measuredDays = trendRows.filter((row) => row.score_pct != null).length;

  // A control with no known achievable score has an unknown gap, so it cannot be
  // ranked. Plotting it at zero gap would rank it as the best-configured control
  // on the board — the flattering direction §8 exists to forbid.
  const rankable = secureScore.worst_controls.filter((control) => control.score_gap != null);
  const unrankable = secureScore.worst_controls.length - rankable.length;
  const controlRows = [...rankable]
    .map(toControlRow)
    .sort((a, b) => b.score_gap - a.score_gap || a.label.localeCompare(b.label));
  const plotted = controlRows.slice(0, RANKED_LIMIT);

  // A group whose points are unknown cannot be ranked or plotted — the same rule
  // as an unrankable control, for the same reason: zero would rank it best.
  const effortRows = secureScore.controls_by_effort
    .filter((group) => group.points_available != null)
    .map(toEffortRow);
  const unmeasuredGroups = secureScore.controls_by_effort.length - effortRows.length;
  const plottedEffort = effortRows.slice(0, RANKED_LIMIT);
  // The biggest prize, which is the caption's whole sentence. Held in a variable
  // so the caption reads it once rather than indexing an array the compiler
  // (rightly) will not promise is non-empty.
  const largestEffort = plottedEffort[0];

  return (
    <section id={SECURE_SCORE_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <SectionHeading
        title="Microsoft Secure Score"
        subtitle={`Microsoft's own assessment of the tenant's security configuration, with ${windowLabel} of daily snapshots behind it. The control catalogue below describes recommended configuration — it names no user, device or mailbox in our estate.`}
      />

      <DefenderErrorsAlert errors={secureScore.errors} />

      <PanelCard
        title="Where the score stands"
        caption={
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span>
              {secureScore.controls_total == null
                ? "The control-profile read did not answer, so how many controls were scored is unknown"
                : `${formatCount(secureScore.controls_total)} controls scored in the latest snapshot`}
            </span>
            {/* Coverage beside the number (§5): the score is a ratio over a
                population, and the population is the thing that moves. */}
            <span>
              ·{" "}
              {secureScore.licensed_user_count == null
                ? "Microsoft did not report how many licensed users it was measured across"
                : `measured across ${formatCount(secureScore.licensed_user_count)} licensed users`}
            </span>
          </span>
        }
      >
        <div className="flex flex-col gap-5">
          {secureScore.score_pct == null ? (
            <Alert tone="warning" title="No current score could be read">
              The score read did not answer, so there is no figure to show — not a zero, and not
              last week&apos;s number carried forward. A Secure Score of zero would be a finding of
              its own, which is exactly why it is not what an unread score renders as.
            </Alert>
          ) : (
            <Meter.Root value={secureScore.score_pct} min={0} max={100} size="lg" tone="accent">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <Meter.Label>Achieved as a share of achievable</Meter.Label>
                <Meter.Value />
              </div>
              <Meter.Track>
                <Meter.Indicator />
              </Meter.Track>
            </Meter.Root>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Statistic
              label="Current score"
              value={formatPoints(secureScore.current_score)}
              tone={secureScore.current_score == null ? "neutral" : "accent"}
              accent
              caption={
                secureScore.current_score == null
                  ? "The score read failed — unknown, not zero points"
                  : `of ${formatPoints(secureScore.max_score)} achievable points`
              }
            />
            <Statistic
              label="Change over the window"
              value={formatPointsDelta(secureScore.score_delta)}
              // A null delta is neutral, never green: nothing was compared.
              tone={
                secureScore.score_delta == null
                  ? "neutral"
                  : secureScore.score_delta > 0
                    ? "success"
                    : secureScore.score_delta < 0
                      ? "warning"
                      : "neutral"
              }
              accent
              caption={
                secureScore.score_delta == null
                  ? "Fewer than two snapshots were read, so no change was measured — this is unknown, not steady"
                  : `Points against the oldest snapshot in the window (${windowLabel})`
              }
            />
            <Statistic
              label="Achieved"
              value={formatPercent(secureScore.score_pct)}
              tone={secureScore.score_pct == null ? "neutral" : "accent"}
              accent
              caption={
                secureScore.score_pct == null
                  ? "The score read failed — unknown, not zero per cent"
                  : "Microsoft's own ratio; it carries no NOVA-set target, so it is not toned as pass or fail"
              }
            />
            <Statistic
              label="Licensed users measured"
              value={formatCount(secureScore.licensed_user_count)}
              // Coverage, not a verdict: a bigger tenant is not a worse one.
              tone={secureScore.licensed_user_count == null ? "neutral" : "accent"}
              accent
              delta={
                secureScore.licensed_user_delta == null
                  ? undefined
                  : `${formatPointsDelta(secureScore.licensed_user_delta)} users`
              }
              caption={
                secureScore.licensed_user_count == null
                  ? "Microsoft did not report the population — unknown, not an empty tenant"
                  : secureScore.licensed_user_delta == null
                    ? "Fewer than two snapshots reported a population, so no change was measured — unknown, not steady"
                    : "The population behind the score. A jump here usually explains a move nobody configured."
              }
            />
          </div>
        </div>
      </PanelCard>

      <ChartCard<ScoreRow>
        title="Secure Score trend"
        caption={
          trendRows.length === 0
            ? "No snapshot was read"
            : `${formatCount(measuredDays)} daily snapshots across ${formatCount(
                trendRows.length,
              )} days · a day Microsoft has no snapshot for is a gap, never a drop to zero`
        }
        since={since}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: TWIN_IDS.scoreTrend,
          ariaLabel: "Every daily Secure Score snapshot in the window",
          data: trendRows,
          getRowId: (row) => row.day,
          columns: [
            { id: "day", header: "Day (UTC)", accessor: (row) => row.label },
            {
              id: "score",
              header: "Score",
              align: "end",
              accessor: (row) => row.current_score,
              cell: (row) => (
                <span className="tabular-nums">{formatPoints(row.current_score)}</span>
              ),
            },
            {
              id: "max",
              header: "Achievable",
              align: "end",
              accessor: (row) => row.max_score,
              cell: (row) => <span className="tabular-nums">{formatPoints(row.max_score)}</span>,
            },
            {
              id: "pct",
              header: "Achieved",
              align: "end",
              accessor: (row) => row.score_pct,
              cell: (row) => <span className="tabular-nums">{formatPercent(row.score_pct)}</span>,
            },
            {
              id: "users",
              header: "Licensed users",
              align: "end",
              accessor: (row) => row.licensed_user_count,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.licensed_user_count)}</span>
              ),
            },
          ],
        }}
      >
        {trendRows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No Secure Score snapshot in this window"
            description="Microsoft returned no daily snapshot for the window. Nothing is drawn — a flat line would claim a score that was never read."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Chart.Container
              config={{ score_pct: { label: "Achieved", color: "var(--norican-accent)" } }}
              className="h-64"
              aria-label={`Microsoft Secure Score achieved as a percentage of achievable, daily, ${windowLabel}`}
            >
              <Chart.LineChart data={trendRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis {...Chart.axisProps} dataKey="label" />
                <Chart.YAxis
                  {...Chart.axisProps}
                  width={48}
                  domain={["auto", "auto"] as const}
                  tickFormatter={(value: number) => `${value}%`}
                />
                <Chart.Tooltip
                  content={
                    // A day with no snapshot arrives as `undefined`; `tooltipValue`
                    // turns that into `null` so the formatter renders an em dash
                    // rather than `Number(null)` — a fabricated 0%.
                    <Chart.TooltipContent
                      formatter={(value) => formatPercent(tooltipValue(value))}
                    />
                  }
                />
                <Chart.Line
                  {...Chart.seriesDefaults}
                  dataKey="score_pct"
                  type="monotone"
                  stroke="var(--color-score_pct)"
                  strokeWidth={2}
                  dot={false}
                />
              </Chart.LineChart>
            </Chart.Container>

            {measuredDays < trendRows.length && (
              <p className="text-fg-muted text-sm">
                {formatCount(trendRows.length - measuredDays)} of {formatCount(trendRows.length)}{" "}
                days in the range have no usable snapshot, so the line breaks there. A break is an
                unmeasured day; it is not a day the score fell.
              </p>
            )}
          </div>
        )}
      </ChartCard>

      <ChartCard<ControlRow>
        title="Controls leaving the most points on the table"
        caption={
          plotted.length === 0
            ? "No control could be ranked"
            : `${formatCount(plotted.length)} of ${formatCount(
                secureScore.controls_total,
              )} scored controls · ranked by points not yet earned`
        }
        since={since}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: TWIN_IDS.worstControls,
          title: `Every control NOVA read (${formatCount(controlRows.length)})`,
          ariaLabel: "Every Secure Score control NOVA read, with its score and its gap",
          data: controlRows,
          getRowId: (row) => row.control,
          columns: [
            { id: "control", header: "Control", accessor: (row) => row.label },
            { id: "id", header: "Microsoft id", accessor: (row) => row.control },
            { id: "category", header: "Category", accessor: (row) => row.category },
            { id: "service", header: "Product", accessor: (row) => row.service },
            { id: "tier", header: "Tier", accessor: (row) => row.tier },
            { id: "impact", header: "User impact", accessor: (row) => row.user_impact },
            { id: "cost", header: "Cost to implement", accessor: (row) => row.implementation_cost },
            {
              id: "score",
              header: "Scored",
              align: "end",
              accessor: (row) => row.score,
              cell: (row) => <span className="tabular-nums">{formatPoints(row.score)}</span>,
            },
            {
              id: "max",
              header: "Achievable",
              align: "end",
              accessor: (row) => row.max_score,
              cell: (row) => <span className="tabular-nums">{formatPoints(row.max_score)}</span>,
            },
            {
              id: "gap",
              header: "Points on the table",
              align: "end",
              accessor: (row) => row.score_gap,
              cell: (row) => <span className="tabular-nums">{formatPoints(row.score_gap)}</span>,
            },
          ],
        }}
      >
        {plotted.length === 0 ? (
          <EmptyState
            size="sm"
            title="No control could be ranked"
            description="Either the control-profile read did not answer — in which case the failure is named above — or every control NOVA read has an unknown achievable score, so no gap can be computed. Neither is the same as a fully-configured tenant."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Chart.Container
              config={{
                score_gap: { label: "Points on the table", color: "var(--norican-accent)" },
              }}
              className="h-80"
              aria-label="Secure Score controls ranked by the points they are leaving unearned"
            >
              <Chart.BarChart
                data={plotted}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={220} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: ControlRow) => [
                        { label: "Points on the table", value: formatPoints(datum.score_gap) },
                        { label: "Scored", value: formatPoints(datum.score) },
                        { label: "Achievable", value: formatPoints(datum.max_score) },
                        { label: "Product", value: datum.service },
                        { label: "User impact", value: datum.user_impact },
                        { label: "Cost to implement", value: datum.implementation_cost },
                      ]}
                    />
                  }
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="score_gap"
                  fill="var(--color-score_gap)"
                  radius={[0, 3, 3, 0]}
                />
              </Chart.BarChart>
            </Chart.Container>

            {controlRows.length > plotted.length && (
              <p className="text-fg-muted text-sm">
                Showing the worst {formatCount(plotted.length)}. View data opens all{" "}
                {formatCount(controlRows.length)} controls NOVA read
                {secureScore.controls_total != null &&
                  secureScore.controls_total > controlRows.length &&
                  `, of ${formatCount(secureScore.controls_total)} the snapshot scored`}
                .
              </p>
            )}

            {unrankable > 0 && (
              <p className="text-fg-muted text-sm">
                {formatCount(unrankable)} further controls have no known achievable score, because
                the control-profile read for them did not answer. Their gap is unknown, so they are
                held out of this ranking rather than plotted at zero — zero would rank them as the
                best-configured controls on the board.
              </p>
            )}
          </div>
        )}
      </ChartCard>

      <ChartCard<EffortRow>
        title="Where the points are, by product and effort"
        caption={
          largestEffort == null
            ? "No group could be sized"
            : // The sentence management acts on, said in words rather than left
              // to be assembled from a ranking.
              `${formatPoints(largestEffort.points_available)} points available in ${
                largestEffort.sentence
              } · ${formatCount(effortRows.length)} groups`
        }
        since={since}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: TWIN_IDS.controlEffort,
          title: `Every product and effort group (${formatCount(effortRows.length)})`,
          ariaLabel:
            "Every Secure Score product and user-impact group, with the points it is holding",
          data: effortRows,
          getRowId: (row) => row.key,
          columns: [
            { id: "service", header: "Product", accessor: (row) => row.service },
            { id: "impact", header: "User impact", accessor: (row) => row.user_impact },
            {
              id: "controls",
              header: "Controls",
              align: "end",
              accessor: (row) => row.controls,
              cell: (row) => <span className="tabular-nums">{formatCount(row.controls)}</span>,
            },
            {
              id: "points",
              header: "Points available",
              align: "end",
              accessor: (row) => row.points_available,
              cell: (row) => (
                <span className="tabular-nums">{formatPoints(row.points_available)}</span>
              ),
            },
          ],
        }}
      >
        {largestEffort == null ? (
          <EmptyState
            size="sm"
            title="No group could be sized"
            description="Either the control-profile read did not answer — in which case the failure is named above — or no group has a known achievable score, so no group has a size. Neither is the same as having no points left to earn."
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Chart.Container
              config={{
                points_available: { label: "Points available", color: "var(--norican-accent)" },
              }}
              className="h-80"
              aria-label="Secure Score points available, grouped by Microsoft product and user impact"
            >
              <Chart.BarChart
                data={plottedEffort}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={220} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: EffortRow) => [
                        {
                          label: "Points available",
                          value: formatPoints(datum.points_available),
                        },
                        { label: "Controls", value: formatCount(datum.controls) },
                        { label: "Product", value: datum.service },
                        { label: "User impact", value: datum.user_impact },
                      ]}
                    />
                  }
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="points_available"
                  fill="var(--color-points_available)"
                  radius={[0, 3, 3, 0]}
                />
              </Chart.BarChart>
            </Chart.Container>

            {effortRows.length > plottedEffort.length && (
              <p className="text-fg-muted text-sm">
                Showing the {formatCount(plottedEffort.length)} largest. View data opens all{" "}
                {formatCount(effortRows.length)} groups.
              </p>
            )}

            {unmeasuredGroups > 0 && (
              <p className="text-fg-muted text-sm">
                {formatCount(unmeasuredGroups)} further groups have no known achievable score, so
                they have no size and are held out of this chart rather than drawn at zero. Zero
                points available would read as work already done.
              </p>
            )}

            <p className="text-fg-muted text-sm">
              Product and user impact are Microsoft&apos;s own catalogue fields, describing a
              recommended configuration rather than anything about this estate — so these groups
              carry no k-anonymity floor, and a group of one control is one recommendation.
            </p>
          </div>
        )}
      </ChartCard>
    </section>
  );
}

/** One product-and-effort group, projected for the chart and the grid. */
function toEffortRow(group: DefenderControlGroup): EffortRow {
  return {
    key: `${group.service}|${group.user_impact}`,
    label: effortAxisLabel(group.service, group.user_impact),
    sentence: effortLabel(group.service, group.user_impact),
    service: group.service,
    user_impact: group.user_impact,
    controls: group.controls,
    // Filtered upstream of here, so the assertion is safe; the fallback keeps a
    // contract change from silently plotting `NaN`.
    points_available: group.points_available ?? 0,
  };
}

/** One control, projected for the chart and the grid. */
function toControlRow(control: DefenderControlScore): ControlRow {
  return {
    control: control.control,
    // The catalogue title is empty when the profile read failed; the Microsoft
    // control id is then the only label there is, so it is used rather than a
    // blank axis tick.
    label: control.title.trim() === "" ? control.control : control.title,
    category: control.category,
    score: control.score,
    max_score: control.max_score,
    // Filtered upstream of here, so the assertion is safe; the fallback keeps a
    // contract change from silently plotting `NaN`.
    score_gap: control.score_gap ?? 0,
    service: control.service,
    tier: control.tier,
    user_impact: control.user_impact,
    implementation_cost: control.implementation_cost,
  };
}

/**
 * Project daily snapshots onto a dense day axis.
 *
 * Days Microsoft has no snapshot for exist as rows with `null` values, so the
 * line breaks over them. Nothing is padded beyond the first and last snapshot:
 * the window NOVA queried is not necessarily the window Microsoft has history
 * for, and inventing leading days would claim otherwise.
 */
function buildScoreRows(trend: readonly DefenderScorePoint[]): ScoreRow[] {
  if (trend.length === 0) {
    return [];
  }
  const byDay = new Map(trend.map((point) => [point.day, point]));
  const days = [...byDay.keys()].sort((a, b) => a.localeCompare(b));
  const first = Date.parse(`${days[0]}T00:00:00Z`);
  const last = Date.parse(`${days[days.length - 1]}T00:00:00Z`);

  const row = (day: string): ScoreRow => {
    const point = byDay.get(day);
    return {
      day,
      label: formatDay(day),
      score_pct: point?.score_pct ?? null,
      current_score: point?.current_score ?? null,
      max_score: point?.max_score ?? null,
      licensed_user_count: point?.licensed_user_count ?? null,
    };
  };

  // A day that will not parse cannot be placed on an axis; plot exactly what
  // arrived rather than guessing where it belongs.
  if (Number.isNaN(first) || Number.isNaN(last) || last < first) {
    return days.map(row);
  }

  const rows: ScoreRow[] = [];
  const DAY_MS = 86_400_000;
  for (let instant = first; instant <= last; instant += DAY_MS) {
    rows.push(row(new Date(instant).toISOString().slice(0, 10)));
  }
  return rows;
}
